
import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import cx from 'classnames';

import { Icon, FormModal, TruncatedLabel } from '../../../../../ui-kit';
import { notify } from '../../../../Notifications';
import FolderIcon from '../../FolderIcon';
import {
  buildTagTree,
  buildSubtreeCounts,
  matchNodesForSearch,
  sanitizeSegment,
  parentLabel,
  lastSegment,
  splitLabel,
  ancestorPaths,
  toggleInSet } from
'../../../utils';
import '../styles/_tag-sidebar.scss';


const ALL_ASSETS_TAG = '__ALL__';
const MODAL_TEXT_STYLE = { color: '#696d74', fontSize: '14px', margin: 0, lineHeight: 'normal' };

// Persist the folder-panel expand preference across sessions. Wrapped in
// try/catch so private-mode / quota failures degrade to the collapsed default.
const EXPANDED_STORAGE_KEY = 'assetLibrary.tagSidebar.expanded';
const readExpandedPref = () => {
  try {
    return localStorage.getItem(EXPANDED_STORAGE_KEY) === 'true';
  } catch (e) {
    return false;
  }
};
const writeExpandedPref = (value) => {
  try {
    localStorage.setItem(EXPANDED_STORAGE_KEY, value ? 'true' : 'false');
  } catch (e) {
    // ignore storage failures
  }
};
const INDENT_BASE = 16;
const INDENT_STEP = 14;
// Hard cap on folder nesting: number of path segments (e.g. acme/2024/final).
// Beyond this the "new subfolder" affordance is hidden and deep labels rejected,
// and indentation stops growing so folder names stay readable.
const MAX_FOLDER_LEVELS = 3;

// "1 asset" / "0 assets" — for the count-pill tooltips.
const assetsLabel = (count) => `${count} ${count === 1 ? 'asset' : 'assets'}`;

// Count real (non-placeholder) folders nested beneath a node.
const countDescendantTags = (node) => {
  if (!node) {return 0;}
  let total = 0;
  node.children.forEach((child) => {
    if (!child.isPlaceholder) {total += 1;}
    total += countDescendantTags(child);
  });
  return total;
};


const TagSidebar = ({
  tags,
  selectedTags,
  isAllAssetsActive,
  onTagSelect,
  onTagCreate,
  onTagRename,
  onTagDelete,
  onDropAsset,
  assets = [],
  assetTagMap = {},
  mobileOpen = false,
  onEditingChange,
  onMobileClose
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [renamingTagId, setRenamingTagId] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [newTagName, setNewTagName] = useState('');
  const [duplicateShake, setDuplicateShake] = useState(false);
  const [renameShake, setRenameShake] = useState(false);
  const [dropTargetTagId, setDropTargetTagId] = useState(null);
  const [, setDragCounter] = useState(0);
  const [animatingTag, setAnimatingTag] = useState(null);
  const [countAdjustments, setCountAdjustments] = useState({});
  const [showEditConfirm, setShowEditConfirm] = useState(false);
  const [deleteConfirmTag, setDeleteConfirmTag] = useState(null);
  const [pendingNewTag, setPendingNewTag] = useState(false);
  const [newlyCreatedTag, setNewlyCreatedTag] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [isExpanded, setIsExpanded] = useState(() => readExpandedPref());
  const [expandedPaths, setExpandedPaths] = useState(() => new Set());
  const [creatingChildOf, setCreatingChildOf] = useState(null);
  const [childName, setChildName] = useState('');
  const clickBlockedRef = useRef(false);
  const newTagRef = useRef(null);
  const listRef = useRef(null);
  const sidebarRef = useRef(null);

  const toggleExpanded = useCallback(() => {
    setIsExpanded((prev) => {
      const next = !prev;
      writeExpandedPref(next);
      return next;
    });
  }, []);

  // Listen for tag events from the context menu (via DOM events to avoid React re-renders)
  useEffect(() => {
    const handleTagAnimated = (e) => {
      setAnimatingTag(e.detail);
      setTimeout(() => setAnimatingTag(null), 500);
    };
    const handleCountChanged = (e) => {
      const { tag, delta } = e.detail;
      setCountAdjustments((prev) => ({ ...prev, [tag]: (prev[tag] || 0) + delta }));
    };
    document.addEventListener('tag-animated', handleTagAnimated);
    document.addEventListener('tag-count-changed', handleCountChanged);
    return () => {
      document.removeEventListener('tag-animated', handleTagAnimated);
      document.removeEventListener('tag-count-changed', handleCountChanged);
    };
  }, []);

  // Reset local adjustments when tags prop updates (parent synced from server)
  useEffect(() => {
    setCountAdjustments({});
  }, [tags]);

// Scroll newly created folder into view once it appears in the rendered list
  useEffect(() => {
    if (newlyCreatedTag && newTagRef.current) {
      newTagRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [newlyCreatedTag, tags]);

  // Notify parent when edit mode changes
  useEffect(() => {
    if (onEditingChange) {onEditingChange(isEditing);}
  }, [isEditing, onEditingChange]);

  // Keep the branch leading to any selected folder expanded so a nested
  // selection is always revealed.
  useEffect(() => {
    if (!selectedTags.length) {return;}
    setExpandedPaths((prev) => {
      const next = new Set(prev);
      selectedTags.forEach((id) => {
        const tag = tags.find((t) => t.tagId === id);
        if (tag) {ancestorPaths(tag.label).forEach((p) => next.add(p));}
      });
      return next;
    });
  }, [selectedTags, tags]);

  // Listen for exit-edit event from content overlay click
  useEffect(() => {
    const handleExitEdit = () => {
      if (isEditing) {
        setPendingNewTag(false);
        setShowEditConfirm(true);
      }
    };
    document.addEventListener('sidebar-exit-edit', handleExitEdit);
    return () => document.removeEventListener('sidebar-exit-edit', handleExitEdit);
  }, [isEditing]);

  // Open the inline create input when the asset context menu's empty-state
  // "+ Create Folder" is triggered (no folders exist yet).
  useEffect(() => {
    const handleOpenCreate = () => setIsCreating(true);
    document.addEventListener('open-create-folder', handleOpenCreate);
    return () => document.removeEventListener('open-create-folder', handleOpenCreate);
  }, []);

  const handleItemClick = useCallback((tagIdOrAll, e) => {
    if (clickBlockedRef.current) {return;}
    const isMulti = e.metaKey || e.ctrlKey;
    onTagSelect(tagIdOrAll, isMulti);
    if (onMobileClose) {onMobileClose();}
  }, [onTagSelect, onMobileClose]);

  const toggleExpand = useCallback((path, e) => {
    if (e) {e.stopPropagation();}
    setExpandedPaths((prev) => toggleInSet(prev, path));
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  }, []);

  const handleDragEnter = useCallback((tagId) => {
    setDragCounter((prev) => prev + 1);
    setDropTargetTagId(tagId);
  }, []);

  const handleDragLeave = useCallback(() => {
    setDragCounter((prev) => {
      const next = prev - 1;
      if (next <= 0) {
        setDropTargetTagId(null);
        return 0;
      }
      return next;
    });
  }, []);

  const handleDrop = useCallback((tagObj, e) => {
    e.preventDefault();
    e.stopPropagation();
    setDropTargetTagId(null);
    setDragCounter(0);

    try {
      const data = JSON.parse(e.dataTransfer.getData('application/json'));
      if (data && data.assetIds && data.assetIds.length > 0) {
        const assetMap = new Map(assets.map((a) => [a.assetId, a]));

        const alreadyTagged = data.assetIds.filter((id) => {
          const tagIds = assetTagMap[id] || [];
          return tagIds.includes(tagObj.tagId);
        });
        const newAssetIds = data.assetIds.filter((id) => !alreadyTagged.includes(id));

        const getAssetName = (id) => {
          const asset = assetMap.get(id);
          const name = asset && (asset.title || asset.filename) || 'Untitled';
          return name.length > 30 ? `${name.substring(0, 27)}...` : name;
        };

        if (alreadyTagged.length === data.assetIds.length) {
          const count = data.assetIds.length;
          const names = alreadyTagged.map(getAssetName);
          notify({
            content: count === 1 ?
            `"${names[0]}" is already in this folder` :
            `All ${count} assets are already in this folder (${names.slice(0, 3).join(', ')}${count > 3 ? '...' : ''})`,
            type: 'info',
            title: 'Already tagged'
          });
          return;
        }

        if (alreadyTagged.length > 0) {
          const dupeNames = alreadyTagged.map(getAssetName);
          notify({
            content: alreadyTagged.length === 1 ?
            `"${dupeNames[0]}" was already in this folder and wasn't added again` :
            `${alreadyTagged.length} assets were already tagged (${dupeNames.slice(0, 3).join(', ')}${alreadyTagged.length > 3 ? '...' : ''})`,
            type: 'info',
            title: 'Some already tagged'
          });
          onDropAsset(tagObj.tagId, newAssetIds);
          return;
        }

        onDropAsset(tagObj.tagId, data.assetIds);
      }
    } catch (err) {

      // Not a valid asset drag — ignore
    }}, [onDropAsset, assets, assetTagMap]);

  const handleRenameStart = useCallback((tagObj) => {
    if (clickBlockedRef.current) {return;}
    setRenamingTagId(tagObj.tagId);
    // Edit only the last path segment; the parent prefix is preserved on submit.
    setRenameValue(lastSegment(tagObj.label));
  }, []);

  const renameSubmittedRef = useRef(false);

  const handleRenameSubmit = useCallback(() => {
    if (renameSubmittedRef.current) {return;}
    renameSubmittedRef.current = true;
    clickBlockedRef.current = true;

    const renamingTagObj = tags.find((t) => t.tagId === renamingTagId);
    const seg = sanitizeSegment(renameValue);
    if (renamingTagId && seg && renamingTagObj &&
    seg !== lastSegment(renamingTagObj.label)) {
      const parent = parentLabel(renamingTagObj.label);
      const newLabel = parent ? `${parent}/${seg}` : seg;
      // Duplicate check is scoped to siblings — comparing full labels means only
      // a folder under the same parent can collide.
      if (tags.some((t) => t.tagId !== renamingTagId && t.label === newLabel)) {
        notify({
          content: `A folder named "${seg}" already exists here`,
          type: 'info',
          title: 'Duplicate folder'
        });
        setRenameShake(true);
        setTimeout(() => setRenameShake(false), 600);
        renameSubmittedRef.current = false;
        clickBlockedRef.current = false;
        return;
      }
      onTagRename(renamingTagId, newLabel);
    }
    setRenamingTagId(null);
    setRenameValue('');

    setTimeout(() => {renameSubmittedRef.current = false;}, 100);
    setTimeout(() => {clickBlockedRef.current = false;}, 300);
  }, [renamingTagId, renameValue, onTagRename, tags]);

  const handleRenameKeyDown = useCallback((e) => {
    e.stopPropagation();
    if (e.key === 'Enter') {
      e.preventDefault();
      handleRenameSubmit();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      renameSubmittedRef.current = true;
      clickBlockedRef.current = true;
      setRenamingTagId(null);
      setRenameValue('');
      setTimeout(() => {renameSubmittedRef.current = false;}, 100);
      setTimeout(() => {clickBlockedRef.current = false;}, 300);
    }
  }, [handleRenameSubmit]);

  const handleDeleteTag = useCallback((tagObj) => {
    setDeleteConfirmTag(tagObj);
  }, []);

  const confirmDeleteTag = useCallback(() => {
    if (!deleteConfirmTag) {return;}
    onTagDelete(deleteConfirmTag.tagId);
    setDeleteConfirmTag(null);
    if (tags.length <= 1) {
      setIsEditing(false);
      setShowEditConfirm(false);
      setRenamingTagId(null);
      setRenameValue('');
    }
  }, [deleteConfirmTag, onTagDelete, tags]);

  const handleCreateSubmit = useCallback(() => {
    const trimmed = sanitizeSegment(newTagName);
    if (!trimmed) {
      setNewTagName('');
      setIsCreating(false);
      return;
    }

    if (tags.some((t) => t.label === trimmed)) {
      if (!duplicateShake) {
        notify({
          content: `A folder named "${trimmed}" already exists`,
          type: 'info',
          title: 'Duplicate folder'
        });
      }
      setDuplicateShake(true);
      setTimeout(() => setDuplicateShake(false), 600);
      return;
    }

    onTagCreate(trimmed);
    setNewlyCreatedTag(trimmed);
    setTimeout(() => setNewlyCreatedTag(null), 800);
    setNewTagName('');
    setIsCreating(false);
  }, [newTagName, onTagCreate, tags, duplicateShake]);

  const handleCreateKeyDown = useCallback((e) => {
    if (e.key === 'Enter') {
      handleCreateSubmit();
    } else if (e.key === 'Escape') {
      setNewTagName('');
      setIsCreating(false);
    }
  }, [handleCreateSubmit]);

  const handleStartCreateChild = useCallback((path, e) => {
    if (e) {e.stopPropagation();}
    setCreatingChildOf(path);
    setChildName('');
    setExpandedPaths((prev) => new Set(prev).add(path));
  }, []);

  const handleCreateChildSubmit = useCallback(() => {
    const parent = creatingChildOf;
    const seg = sanitizeSegment(childName);
    if (!parent || !seg) {
      setChildName('');
      setCreatingChildOf(null);
      return;
    }

    if (splitLabel(parent).length >= MAX_FOLDER_LEVELS) {
      notify({
        content: `Folders can be nested up to ${MAX_FOLDER_LEVELS} levels deep`,
        type: 'info',
        title: 'Maximum depth reached'
      });
      setChildName('');
      setCreatingChildOf(null);
      return;
    }

    const fullLabel = `${parent}/${seg}`;
    if (tags.some((t) => t.label === fullLabel)) {
      if (!duplicateShake) {
        notify({
          content: `A folder named "${seg}" already exists here`,
          type: 'info',
          title: 'Duplicate folder'
        });
      }
      setDuplicateShake(true);
      setTimeout(() => setDuplicateShake(false), 600);
      return;
    }

    onTagCreate(fullLabel);
    setNewlyCreatedTag(fullLabel);
    setExpandedPaths((prev) => new Set(prev).add(parent));
    setTimeout(() => setNewlyCreatedTag(null), 800);
    setChildName('');
    setCreatingChildOf(null);
  }, [creatingChildOf, childName, tags, onTagCreate, duplicateShake]);

  const handleChildCreateKeyDown = useCallback((e) => {
    if (e.key === 'Enter') {
      handleCreateChildSubmit();
    } else if (e.key === 'Escape') {
      setChildName('');
      setCreatingChildOf(null);
    }
  }, [handleCreateChildSubmit]);

  // Clear drop highlight when any drag operation ends (safety net)
  useEffect(() => {
    const clearDrag = () => {
      setDropTargetTagId(null);
      setDragCounter(0);
    };
    document.addEventListener('dragend', clearDrag);
    return () => document.removeEventListener('dragend', clearDrag);
  }, []);

  const isAllSelected = typeof isAllAssetsActive === 'boolean' ?
  isAllAssetsActive :
  selectedTags.length === 0;

  const tree = useMemo(() => buildTagTree(tags), [tags]);
  const subtreeCounts = useMemo(
    () => buildSubtreeCounts(tree.roots, countAdjustments),
    [tree, countAdjustments]
  );

  // When searching, only matching branches (matches + their ancestors) are
  // shown, and those branches are force-expanded for this render.
  const visiblePaths = searchQuery ? matchNodesForSearch(tags, searchQuery) : null;
  const effectiveExpanded = searchQuery ? visiblePaths : expandedPaths;

  const renderChildCreateInput = (parentDepth) =>
  <div
    className={cx('tag-sidebar__create-input', 'tag-sidebar__create-input--child', {
      'tag-sidebar__create-input--shake': duplicateShake
    })}
    style={{ paddingLeft: `${INDENT_BASE + Math.min(parentDepth + 1, MAX_FOLDER_LEVELS - 1) * INDENT_STEP}px` }}>

      <input
      type="text"
      value={childName}
      onChange={(e) => setChildName(e.target.value)}
      onKeyDown={handleChildCreateKeyDown}
      onBlur={() => {setChildName('');setCreatingChildOf(null);}}
      placeholder="Subfolder name..."
      ref={(input) => input && input.focus()} />

    </div>;

  const renderNode = (node) => {
    if (visiblePaths && !visiblePaths.has(node.path)) {return null;}

    const hasChildren = node.children.length > 0;
    const isExpanded = effectiveExpanded.has(node.path);
    const isActive = Boolean(node.tagId) && selectedTags.includes(node.tagId);
    const isDropTarget = Boolean(node.tagId) && dropTargetTagId === node.tagId;
    const isRenaming = isEditing && renamingTagId === node.tagId;
    const showCount = !isEditing && !node.isPlaceholder;
    // Optimistic deltas are applied blind, so a stale one can drive a count
    // below zero before the next server refetch lands. Never render that.
    const directCount = Math.max(0, node.directCount + (countAdjustments[node.path] || 0));
    const totalCount = Math.max(0, subtreeCounts.get(node.path) || 0);
    // The rollup pill only earns its place when the subfolders actually add
    // assets — otherwise the two pills would show the same number twice. A
    // placeholder (an intermediate path with no tag row of its own) has no
    // direct count, so the rollup is the only thing it can meaningfully show.
    const showTotal = !isEditing && hasChildren && totalCount > directCount;
    // Whenever the rollup shows, the direct pill shows too — even at zero.
    // Selecting a folder filters to its OWN assets, so a lone "12" would
    // promise a gridful and then render an empty one. "12 / 0" tells the truth.
    const showDirect = showCount && (directCount > 0 || showTotal);
    const indentLevel = Math.min(node.depth, MAX_FOLDER_LEVELS - 1);
    const canAddSubfolder = node.depth < MAX_FOLDER_LEVELS - 1;

    const dragProps = node.isPlaceholder ? {} : {
      onDragOver: handleDragOver,
      onDragEnter: () => handleDragEnter(node.tagId),
      onDragLeave: handleDragLeave,
      onDrop: (e) => handleDrop(node.tag, e)
    };

    return (
      <React.Fragment key={node.path}>
        <div
          ref={newlyCreatedTag === node.path ? newTagRef : null}
          className={cx('tag-sidebar__item', {
            'tag-sidebar__item--active': isActive,
            'tag-sidebar__item--drop-target': isDropTarget,
            'tag-sidebar__item--editing': isEditing && !node.isPlaceholder,
            'tag-sidebar__item--placeholder': node.isPlaceholder,
            'tag-sidebar__item--new': newlyCreatedTag === node.path
          })}
          style={{ paddingLeft: `${INDENT_BASE + indentLevel * INDENT_STEP}px` }}
          onClick={(e) => {
            if (node.isPlaceholder) {toggleExpand(node.path, e);return;}
            handleItemClick(node.tagId, e);
          }}
          onKeyDown={!isEditing ? (e) => e.key === 'Enter' && !node.isPlaceholder && handleItemClick(node.tagId, e) : undefined}
          role="button"
          tabIndex={0}
          {...dragProps}>

          {hasChildren ?
          <span
            className="tag-sidebar__item-chevron"
            onClick={(e) => toggleExpand(node.path, e)}
            onKeyDown={(e) => {if (e.key === 'Enter') {toggleExpand(node.path, e);}}}
            role="button"
            tabIndex={0}
            title={isExpanded ? 'Collapse' : 'Expand'}>

              <Icon name={isExpanded ? 'chevrondown' : 'chevronright'} size={12} color="regent" />
            </span> :

          <span className="tag-sidebar__item-spacer" />}

          <FolderIcon className="tag-sidebar__item-icon" />
          {isRenaming ?
          <input
            className={cx('tag-sidebar__rename-input', { 'tag-sidebar__rename-input--shake': renameShake })}
            type="text"
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            onKeyDown={handleRenameKeyDown}
            ref={(input) => input && input.focus()}
            onClick={(e) => e.stopPropagation()} /> :

          <TruncatedLabel
            id={`tag-sidebar-label-${encodeURIComponent(node.path)}`}
            text={node.segment}
            className="tag-sidebar__item-label" />}

          {isEditing && !isRenaming && !node.isPlaceholder &&
          <span className="tag-sidebar__item-actions">
            <span
              className="tag-sidebar__item-action"
              onClick={(e) => {e.stopPropagation();handleRenameStart(node.tag);}}
              onKeyDown={(e) => {if (e.key === 'Enter') {e.stopPropagation();handleRenameStart(node.tag);}}}
              role="button"
              tabIndex={0}
              title="Rename">

              <Icon name="edit" size={14} color="regent" hoverColor="white" />
            </span>
            <span
              className="tag-sidebar__item-action tag-sidebar__item-action--delete"
              onClick={(e) => {e.stopPropagation();handleDeleteTag(node.tag);}}
              onKeyDown={(e) => {if (e.key === 'Enter') {e.stopPropagation();handleDeleteTag(node.tag);}}}
              role="button"
              tabIndex={0}
              title="Delete">

              <Icon name="trash" size={14} color="regent" hoverColor="white" />
            </span>
          </span>}

          {!isEditing && !node.isPlaceholder && canAddSubfolder &&
          <button
            type="button"
            className="tag-sidebar__subfolder-btn"
            onClick={(e) => handleStartCreateChild(node.path, e)}
            title="New subfolder">

            +
          </button>}

          {(showTotal || showDirect) &&
          <span className="tag-sidebar__item-counts">
            {showTotal &&
            <span
              className="tag-sidebar__item-count tag-sidebar__item-count--total"
              title={`${assetsLabel(totalCount)} in this folder and its subfolders`}>

              {totalCount}
            </span>}

            {showDirect &&
            <span
              className={cx('tag-sidebar__item-count', {
                'tag-sidebar__item-count--added': animatingTag && animatingTag.tag === node.path && animatingTag.type === 'added',
                'tag-sidebar__item-count--removed': animatingTag && animatingTag.tag === node.path && animatingTag.type === 'removed'
              })}
              title={`${assetsLabel(directCount)} in this folder`}>

              {directCount}
            </span>}

          </span>}

        </div>

        {creatingChildOf === node.path && renderChildCreateInput(node.depth)}
        {isExpanded && node.children.map(renderNode)}
      </React.Fragment>);

  };

  return (
    <>
      {mobileOpen && onMobileClose &&
      <div
        className="tag-sidebar-mobile-backdrop"
        onClick={onMobileClose}
        role="presentation" />}


      <div
        ref={sidebarRef}
        className={cx('tag-sidebar', {
          'tag-sidebar--mobile-open': mobileOpen,
          'tag-sidebar--expanded': isExpanded
        })}>
        <div className="tag-sidebar__header">
          <span className="tag-sidebar__header-label">Folders</span>
          <div className="tag-sidebar__header-actions">
            <button
              type="button"
              className="tag-sidebar__expand-btn"
              onClick={toggleExpanded}
              aria-pressed={isExpanded}
              title={isExpanded ? 'Collapse panel' : 'Expand panel'}>

              <Icon name={isExpanded ? 'tabcollapse' : 'tabexpand'} size={14} color="regent" hoverColor="white" />
            </button>
            {tags.length > 0 &&
            <button
              type="button"
              className={cx('tag-sidebar__edit-btn', { 'tag-sidebar__edit-btn--active': isEditing })}
              onClick={() => {setIsEditing(!isEditing);setRenamingTagId(null);setRenameValue('');}}
              title={isEditing ? 'Done editing' : 'Edit folders'}>

              {isEditing ? <span className="tag-sidebar__edit-btn-label">Done</span> : <Icon name="edit" size={14} color="regent" hoverColor="white" />}
            </button>}
          </div>

        </div>

        {tags.length > 5 &&
        <div className="tag-sidebar__search">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search folders..."
            className="tag-sidebar__search-input" />

        </div>}


        <div className="tag-sidebar__new-folder">
          {isCreating ?
          <div className={cx('tag-sidebar__create-input', { 'tag-sidebar__create-input--shake': duplicateShake })}>
              <input
              type="text"
              value={newTagName}
              onChange={(e) => setNewTagName(e.target.value)}
              onKeyDown={handleCreateKeyDown}
              onBlur={() => {setNewTagName('');setIsCreating(false);}}
              placeholder="Folder name..."
              ref={(input) => input && input.focus()} />

            </div> :

          <button
            type="button"
            className={cx('tag-sidebar__item', 'tag-sidebar__add-btn', {
              'tag-sidebar__add-btn--shake': duplicateShake
            })}
            onClick={() => {
              if (isEditing) {
                setPendingNewTag(true);
                setShowEditConfirm(true);
              } else {
                setIsCreating(true);
              }
            }}
            title="New Folder">

              <span className="tag-sidebar__add-icon">+</span>
              <span className="tag-sidebar__item-label">New Folder</span>
            </button>}
        </div>

        <div className="tag-sidebar__list" ref={listRef}>
          <button
            type="button"
            className={cx('tag-sidebar__item', 'tag-sidebar__item--all', {
              'tag-sidebar__item--active': isAllSelected
            })}
            onClick={(e) => handleItemClick(ALL_ASSETS_TAG, e)}
            title="All Assets">

            <span className="tag-sidebar__item-spacer" />
            <FolderIcon className="tag-sidebar__item-icon" />
            <span className="tag-sidebar__item-label">All Assets</span>
          </button>

          {tree.roots.map(renderNode)}

        </div>

      </div>

      {showEditConfirm &&
      <FormModal
        visible
        title="Are you done editing your Tags?"
        onClose={() => {setShowEditConfirm(false);setPendingNewTag(false);}}
        onSave={() => {
          setIsEditing(false);
          setShowEditConfirm(false);
          setRenamingTagId(null);
          setRenameValue('');
          if (pendingNewTag) {
            setPendingNewTag(false);
            setIsCreating(true);
          }
        }}
        saveText="Yes, I'm done">

          <span style={MODAL_TEXT_STYLE}>
            You can continue editing or exit edit mode.
          </span>
        </FormModal>}


      {deleteConfirmTag && (() => {
        const node = tree.byPath.get(deleteConfirmTag.label);
        const childCount = countDescendantTags(node);
        const seg = lastSegment(deleteConfirmTag.label);
        return (
          <FormModal
            visible
            title={childCount > 0 ? `Delete "${seg}" and its subfolders?` : `Delete folder "${seg}"?`}
            onClose={() => setDeleteConfirmTag(null)}
            onSave={confirmDeleteTag}
            saveText="Delete Folder">

            <span style={MODAL_TEXT_STYLE}>
              {childCount > 0 ?
              `This will remove this folder, its ${childCount} subfolder${childCount > 1 ? 's' : ''}, and untag all assets in them. Your assets will not be deleted.` :
              'This will remove the folder and untag all assets in it. Your assets will not be deleted.'}
            </span>
          </FormModal>);

      })()}

    </>);

};

export default TagSidebar;
